import { useEffect, useState } from "react";

export interface ToastMessage {
  id: string;
  message: string;
  kind: "success" | "error" | "info";
}

interface ToastProps {
  toasts: ToastMessage[];
  onDismiss: (id: string) => void;
}

export function Toast({ toasts, onDismiss }: ToastProps) {
  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-2 w-full max-w-sm px-4 pointer-events-none">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastMessage; onDismiss: (id: string) => void }) {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), 4000);
    return () => clearTimeout(timer);
  }, [toast.id, onDismiss]);

  const bg =
    toast.kind === "success"
      ? "bg-green-600"
      : toast.kind === "error"
        ? "bg-red-600"
        : "bg-gray-800";

  return (
    <div
      className={`${bg} text-white text-sm font-mono py-2.5 px-4 rounded-lg shadow-lg pointer-events-auto flex items-center gap-2`}
    >
      <span className="flex-1">{toast.message}</span>
      <button onClick={() => onDismiss(toast.id)} className="opacity-70 hover:opacity-100 ml-1 text-xs">
        ✕
      </button>
    </div>
  );
}

// Hook for toast management
let _addToast: ((msg: Omit<ToastMessage, "id">) => void) | null = null;

export function useToastManager() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = (msg: Omit<ToastMessage, "id">) => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { ...msg, id }]);
  };

  const dismissToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  useEffect(() => {
    _addToast = addToast;
    return () => { _addToast = null; };
  });

  return { toasts, addToast, dismissToast };
}

export function toast(msg: Omit<ToastMessage, "id">) {
  _addToast?.(msg);
}
